export interface HighlightSegment {
  text: string;
  highlighted: boolean;
}

/**
 * Collapse whitespace runs to a single space, trim, and lowercase. Mirrors the
 * API's section matcher (`normalize` in apps/api/src/lib/mention-evidence.ts) so
 * the client agrees with which section the server picked.
 */
function normalizeWhitespace(value: string): string {
  return value.replace(/\s+/g, ' ').trim().toLowerCase();
}

const REGEX_SPECIALS = /[.*+?^${}()|[\]\\]/g;

/**
 * Split `haystack` into segments with the first occurrence of `needle` highlighted.
 * Matching is case-insensitive and whitespace-tolerant: a whitespace run in the
 * needle matches any whitespace run in the haystack, so an excerpt written with
 * single spaces still highlights a section that wraps it across newlines.
 */
export function buildHighlightSegments(haystack: string, needle: string): HighlightSegment[] {
  if (!haystack) return [];
  const trimmedNeedle = needle.trim();
  if (!trimmedNeedle) return [{ text: haystack, highlighted: false }];
  const pattern = trimmedNeedle.replace(REGEX_SPECIALS, '\\$&').replace(/\s+/g, '\\s+');
  const match = new RegExp(pattern, 'i').exec(haystack);
  if (!match) return [{ text: haystack, highlighted: false }];
  const start = match.index;
  const end = start + match[0].length;
  const segments: HighlightSegment[] = [];
  if (start > 0) segments.push({ text: haystack.slice(0, start), highlighted: false });
  segments.push({ text: haystack.slice(start, end), highlighted: true });
  if (end < haystack.length) segments.push({ text: haystack.slice(end), highlighted: false });
  return segments;
}

/**
 * Prefer the section text as the canvas when it contains the excerpt — using the
 * same whitespace-insensitive comparison as the API's section matcher, so a
 * section the server selected isn't discarded here over whitespace differences.
 * Otherwise fall back to the excerpt itself.
 */
export function chooseHaystack(sectionText: string | null, excerpt: string | null): string | null {
  if (
    sectionText &&
    excerpt &&
    normalizeWhitespace(sectionText).includes(normalizeWhitespace(excerpt))
  ) {
    return sectionText;
  }
  return excerpt ?? sectionText ?? null;
}

export interface ExcerptWindow {
  text: string;
  clippedStart: boolean;
  clippedEnd: boolean;
}

const WINDOW_RADIUS = 160;

const WHITESPACE = /\s/;

function findNeedleRange(
  haystack: string,
  needle: string | null,
): { start: number; end: number } | null {
  if (!needle) return null;
  const trimmed = needle.trim();
  if (!trimmed) return null;
  const pattern = trimmed.replace(REGEX_SPECIALS, '\\$&').replace(/\s+/g, '\\s+');
  const match = new RegExp(pattern, 'i').exec(haystack);
  return match ? { start: match.index, end: match.index + match[0].length } : null;
}

/** Advance `index` to the start of the next word, never passing `limit`. */
function snapForward(text: string, index: number, limit: number): number {
  let i = index;
  const midWord = !WHITESPACE.test(text[i - 1] ?? ' ') && !WHITESPACE.test(text[i] ?? ' ');
  if (midWord) {
    while (i < limit && !WHITESPACE.test(text[i] ?? '')) i++;
  }
  while (i < limit && WHITESPACE.test(text[i] ?? '')) i++;
  return i;
}

/** Retreat `index` to the end of the previous word, never passing `limit`. */
function snapBackward(text: string, index: number, limit: number): number {
  let i = index;
  const midWord = !WHITESPACE.test(text[i - 1] ?? ' ') && !WHITESPACE.test(text[i] ?? ' ');
  if (midWord) {
    while (i > limit && !WHITESPACE.test(text[i - 1] ?? '')) i--;
  }
  while (i > limit && WHITESPACE.test(text[i - 1] ?? '')) i--;
  return i;
}

/**
 * Slice a window of ~WINDOW_RADIUS characters either side of the excerpt match
 * so the highlight is immediately visible without rendering the whole section.
 * Uses the same whitespace-tolerant matching as buildHighlightSegments. Falls
 * back to a leading window when the excerpt is missing or not found.
 */
export function buildExcerptWindow(haystack: string, needle: string | null): ExcerptWindow {
  const range = findNeedleRange(haystack, needle);
  const anchorStart = range?.start ?? 0;
  const anchorEnd = range?.end ?? 0;

  let start = anchorStart > WINDOW_RADIUS ? anchorStart - WINDOW_RADIUS : 0;
  if (start > 0) start = snapForward(haystack, start, anchorStart);

  const rawEnd = range ? anchorEnd + WINDOW_RADIUS : 2 * WINDOW_RADIUS;
  let end = Math.min(haystack.length, rawEnd);
  if (end < haystack.length) end = snapBackward(haystack, end, anchorEnd);

  return {
    text: haystack.slice(start, end),
    clippedStart: start > 0,
    clippedEnd: end < haystack.length,
  };
}
